import { Icon } from '@sdb/planning-ui';
import { Box, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import type {
  HoursPayOutRequestResponse,
  HoursPayOutRequestType
} from 'generated/index';
import { formatMinutesInHours } from 'utils/date';
import { dateFormatter } from 'utils/grid';

const useColumnDefinitions = () => {
  'use memo';

  const { t } = useTranslation('common');

  return [
    {
      field: 'employmentNumber',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.employmentNumber'),
      valueGetter: (_, row: HoursPayOutRequestResponse) =>
        row?.employment?.number ?? '',
      minWidth: 170
    },
    {
      field: 'employeeName',
      type: 'string',
      headerName: t('employee'),
      valueGetter: (_, row: HoursPayOutRequestResponse) =>
        row?.employment?.employeeName ?? '',
      minWidth: 220,
      flex: 1
    },
    {
      field: 'requestDate',
      type: 'date',
      headerName: t('hoursPayOutRequestsOverview.requestDate'),
      valueFormatter: dateFormatter,
      width: 150
    },
    {
      field: 'type',
      type: 'string',
      headerName: t('type'),
      valueFormatter: (value: HoursPayOutRequestType) =>
        value ? t(`hoursPayOutRequestsOverview.type.${value}`) : '',
      minWidth: 150
    },
    {
      field: 'amountInMinutes',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.amount'),
      valueFormatter: (value: number) => formatMinutesInHours(value, true),
      width: 120
    },
    {
      field: 'balanceAtRequestInMinutes',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.balanceAtRequest'),
      valueFormatter: (value: number | null) =>
        value === null || value === undefined
          ? ''
          : formatMinutesInHours(value, true),
      width: 160
    },
    {
      field: 'remainingInMinutes',
      type: 'string',
      headerName: t('hoursPayOutRequestsOverview.currentBalance'),
      valueFormatter: (value: number | null) =>
        value === null || value === undefined
          ? ''
          : formatMinutesInHours(value, true),
      width: 140
    },
    {
      field: 'processed',
      type: 'boolean',
      headerName: t('hoursPayOutRequestsOverview.processed'),
      width: 130,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1}>
          {(row.processed || row.status === 'APPROVED') && (
            <Icon type="check" color="success" fontSize="small" />
          )}
          {row.status === 'REJECTED' && row.statusNote && (
            <Tooltip title={row.statusNote}>
              <Box display="inline-flex" alignItems="center">
                <Icon
                  type="triangle-exclamation"
                  color="warning"
                  fontSize="small"
                />
              </Box>
            </Tooltip>
          )}
        </Box>
      )
    }
  ] as GridColDef[];
};

export default useColumnDefinitions;
